/**
 * Token-bound disclosure row for monograph demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";
import { colorWithAlpha } from "./MonographPanelColor.js";
import {
  MonographSurfaceBox,
  type MonographSurfaceBoxElement,
} from "./MonographSurfaceBox.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export interface MonographDisclosureRowProps {
  readonly tokens: MonographPanelTokens;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly primary: React.ReactNode;
  readonly children: React.ReactNode;
  readonly as?: MonographSurfaceBoxElement;
  readonly leading?: React.ReactNode;
  readonly secondary?: React.ReactNode;
  readonly trailing?: React.ReactNode;
  readonly color?: string;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly buttonClassName?: string;
  readonly contentClassName?: string;
  readonly style?: React.CSSProperties;
  readonly buttonStyle?: React.CSSProperties;
  readonly contentStyle?: React.CSSProperties;
  readonly contentFont?: "body" | "mono";
}

function disclosureColumns(hasLeading: boolean, hasTrailing: boolean): string {
  const columns = [];
  if (hasLeading) columns.push("auto");
  columns.push("minmax(0,1fr)");
  if (hasTrailing) columns.push("auto");
  columns.push("auto");
  return columns.join(" ");
}

export function MonographDisclosureRow({
  tokens,
  expanded,
  onToggle,
  primary,
  children,
  as,
  leading,
  secondary,
  trailing,
  color = tokens.accent,
  ariaLabel,
  className,
  buttonClassName,
  contentClassName,
  style,
  buttonStyle,
  contentStyle,
  contentFont = "body",
}: MonographDisclosureRowProps): React.JSX.Element {
  return (
    <MonographSurfaceBox
      tokens={tokens}
      active={expanded}
      color={color}
      background={colorWithAlpha(tokens.void, "70")}
      padding={0}
      overflow="hidden"
      {...(as === undefined ? {} : { as })}
      {...(className === undefined ? {} : { className })}
      {...(style === undefined ? {} : { style })}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={ariaLabel}
        className={cn("csk-monograph-disclosure-row", buttonClassName)}
        style={{
          alignItems: "center",
          background: "transparent",
          border: 0,
          color: tokens.text,
          cursor: "pointer",
          display: "grid",
          gap: 12,
          gridTemplateColumns: disclosureColumns(leading !== undefined, trailing !== undefined),
          padding: "10px 14px",
          textAlign: "left",
          width: "100%",
          ...buttonStyle,
        }}
      >
        {leading}
        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span
            style={{
              color: tokens.bright,
              fontSize: 13,
              fontWeight: 650,
              lineHeight: 1.4,
            }}
          >
            {primary}
          </span>
          {secondary ? (
            <span
              style={{
                color: tokens.muted,
                fontSize: 11.5,
                lineHeight: 1.45,
                marginTop: 2,
              }}
            >
              {secondary}
            </span>
          ) : null}
        </span>
        {trailing}
        <span
          aria-hidden
          style={{
            color: tokens.muted,
            fontFamily: tokens.mono,
            fontSize: 14,
            transform: expanded ? "rotate(90deg)" : "rotate(0)",
            transition: "transform 140ms ease",
          }}
        >
          &rsaquo;
        </span>
      </button>
      {expanded ? (
        <div
          className={contentClassName}
          style={{
            borderTop: `1px dashed ${tokens.border}`,
            color: tokens.text,
            fontFamily: contentFont === "mono" ? tokens.mono : undefined,
            fontSize: 12.5,
            lineHeight: 1.7,
            padding: "10px 16px 14px",
            ...contentStyle,
          }}
        >
          {children}
        </div>
      ) : null}
    </MonographSurfaceBox>
  );
}
